import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from '@material-ui/core'
import useStyles from './styles'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import AccountRouterLink from 'components/account-router-link'
import PaymentAccountWarning from 'components/payment-account-warning'
import ExpirationDurationDropdown from './expiration-duration-dropdown'
import BookingTypePriceQuantity from './booking-type-price-quantity'
import BookingTypePrice from './booking-type-price'
import NoPaymentAccounts from './no-payment-accounts'
import ManageStripeAccountConfirmationDialog from 'pages/account/payments/payment-accounts/manage-stripe-account-confirmation-dialog'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import TipWell from 'components/tip-well'
import { optionalLabel, requiredLabel } from './required-field'

const BookingTypePaymentOptions = ({
  errors,
  control,
  register,
  isLessonPacks,
  isVirtualCoaching = false,
  paymentAccounts,
  singleLessonPrice,
  watch,
  isEditFlow = false,
}) => {
  const classes = useStyles()
  const editClasses = useEditFormStyles()
  const fieldClass = isEditFlow ? editClasses.fieldInput : classes.formField
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )
  const selectedPaymentAccountId = watch?.('paymentAccountId')
  const selectedAccount = paymentAccounts.find(
    acc => acc.id === selectedPaymentAccountId,
  )
  const hasIncompleteStripeSetup =
    selectedAccount &&
    (!selectedAccount.detailsSubmitted || !selectedAccount.chargesEnabled)

  const requiresPaymentAccount = isLessonPacks || isVirtualCoaching

  const paymentAccountLabel = requiresPaymentAccount
    ? requiredLabel('Payment Account')
    : 'Payment Account'

  let paymentAccountHelper =
    'Select a payment account to collect registration fees online for this booking type. If no payment account is selected, upfront payments will not be required.'
  if (isLessonPacks) {
    paymentAccountHelper =
      'Select a payment account to collect online payments for this lesson pack. Upfront payments are required to offer a lesson pack.'
  }
  if (isVirtualCoaching) {
    paymentAccountHelper =
      'Select a payment account to collect online payments for this virtual coaching offering. Students pay when they purchase, before they upload a video.'
  }

  return (
    <>
      {!isLessonPacks ? (
        <BookingTypePrice
          register={register}
          errors={errors}
          isEditFlow={isEditFlow}
          isVirtualCoaching={isVirtualCoaching}
        />
      ) : (
        <>
          <BookingTypePriceQuantity
            singleLessonPrice={singleLessonPrice}
            isEditFlow={isEditFlow}
          />
          <ExpirationDurationDropdown isEditFlow={isEditFlow} />
        </>
      )}
      <Box className={isEditFlow ? editClasses.fieldGroup : classes.formField}>
        {isEditFlow ? (
          <>
            <Typography className={editClasses.sectionHeading}>
              {requiresPaymentAccount
                ? 'Payment Account'
                : optionalLabel('Payment Account')}
            </Typography>
            <Typography className={editClasses.fieldHelper}>
              {paymentAccountHelper}
            </Typography>
          </>
        ) : (
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              {requiresPaymentAccount
                ? 'Payment Account'
                : optionalLabel('Payment Account')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {paymentAccountHelper}
            </Typography>
          </Box>
        )}
        {paymentAccounts.length > 0 || !requiresPaymentAccount ? (
          <>
            <FormControl
              error={!!errors.paymentAccountId || hasIncompleteStripeSetup}
              variant="outlined"
              fullWidth
              className={fieldClass}
            >
              <InputLabel id="payment-account-label">
                {paymentAccountLabel}
              </InputLabel>
              <Controller
                render={({ onChange, value }) => (
                  <Select
                    labelId="payment-account-label"
                    label={paymentAccountLabel}
                    data-cy="paymentAccount"
                    value={value ?? ''}
                    onChange={onChange}
                  >
                    {paymentAccounts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id} data-cy={opt.name}>
                        {opt.name}
                      </MenuItem>
                    ))}
                    <MenuItem key="none" value="">
                      <em>None</em>
                    </MenuItem>
                  </Select>
                )}
                name="paymentAccountId"
                control={control}
              />
              {errors.paymentAccountId ? (
                <FormHelperText>
                  {errors.paymentAccountId.message}
                </FormHelperText>
              ) : null}
            </FormControl>
            <Box mt={1}>
              <Button
                color="primary"
                size="small"
                to="/account/payments/payment-accounts"
                component={AccountRouterLink}
              >
                Manage payment accounts
              </Button>
            </Box>
            <PaymentAccountWarning
              paymentAccount={selectedAccount}
              hasUnresolvedPaymentAccount={
                !!selectedPaymentAccountId && !selectedAccount
              }
              onOpen={() => setIsManageStripeDialogOpen(true)}
            />
          </>
        ) : (
          <NoPaymentAccounts
            errors={errors.paymentAccountId}
            isVirtualCoaching={isVirtualCoaching}
          />
        )}
      </Box>
      <ManageStripeAccountConfirmationDialog
        paymentAccount={selectedAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => setIsManageStripeDialogOpen(false)}
      />

      {paymentAccounts.length > 0 ? (
        <Box
          className={isEditFlow ? editClasses.fieldGroup : classes.formField}
        >
          <TipWell
            variant="neutral"
            icon={InfoOutlinedIcon}
            caption="Please note that a 2.9% + $0.30 fee will be applied to the transaction total."
          />
        </Box>
      ) : null}
    </>
  )
}

export default BookingTypePaymentOptions
